import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FiSearch, FiUser } from 'react-icons/fi';
import Loader from 'react-loader-spinner';
import { API_URLS, getPlayerImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { extractList, playerFullName } from '../utilities/disqualifications';
import CustomModal from './CustomModal';
import defaultPlayerImage from '../images/defaultAvatar.png';
import '../styles/createMatchModal.css';

const PLAYER_LIMIT = 20;

function playerPhoto(player) {
  return getPlayerImageUrl(player?.image) || defaultPlayerImage;
}

export default function AdminPlayerPicker({
  champId,
  leagueId,
  value,
  onChange,
  error,
  disabled,
  /** 'default' | 'icon' — compact search button for inline row pickers */
  variant = 'default',
  title,
}) {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [loadError, setLoadError] = useState('');
  const searchTimer = useRef(null);
  const isIcon = variant === 'icon';

  const selectedLabel = playerFullName(value, t('dqPickPlayer'));

  const playerMeta = (player) => {
    const number = player?.number || player?.player_number || player?.match_number || player?.num;
    const team = player?.team_name || t('ttNoTeam');
    if (number != null && String(number).trim() !== '') {
      return `№${number} · ${team}`;
    }
    return team;
  };

  const loadPlayers = async (search) => {
    if (!champId && (leagueId == null || leagueId === '')) return;
    setLoading(true);
    setLoadError('');
    try {
      const response = await authorizedPost(dispatch, API_URLS.getLeaguePlayers, {
        champ_id: champId || undefined,
        league_id: leagueId == null || leagueId === '' ? 0 : Number(leagueId),
        search: search && search.trim() ? search.trim() : undefined,
        limit: PLAYER_LIMIT,
        offset: 0,
      });
      const list = extractList(response.data, ['players', 'items', 'data']).map((item) => ({
        ...item,
        id: item.id || item.player_id,
      }));
      setPlayers(list);
      setSearched(true);
    } catch (err) {
      setPlayers([]);
      setSearched(true);
      setLoadError(getApiErrorMessage(err, t('dqLoadPlayersError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = query ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadPlayers(query);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [open, query, champId, leagueId]);

  const close = () => {
    setOpen(false);
    setQuery('');
    setLoadError('');
  };

  return (
    <>
      {isIcon ? (
        <button
          type="button"
          className={`ig-clear-btn ig-match-subs-search-btn${error ? ' error' : ''}`.trim()}
          disabled={disabled}
          onClick={() => setOpen(true)}
          title={title || t('igMatchSubsSearchPlayer')}
        >
          <FiSearch />
        </button>
      ) : (
        <button
          type="button"
          className={`st-input dq-picker-btn ${error ? 'error' : ''}`.trim()}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {value ? (
            <span className="dq-picker-current">
              <img src={playerPhoto(value)} alt="" onError={(event) => { event.target.src = defaultPlayerImage; }} />
              <span>
                <strong>{selectedLabel}</strong>
                {value.team_name ? <em>{value.team_name}</em> : null}
              </span>
            </span>
          ) : (
            <span className="dq-picker-placeholder">
              <FiUser /> {t('dqPickPlayer')}
            </span>
          )}
        </button>
      )}

      <CustomModal
        styles={{ width: 480, height: 'auto', paddingLeft: 24, paddingRight: 24, paddingTop: 20, paddingBottom: 20 }}
        visible={open}
        closeModal={close}
      >
        <div className="dq-picker-modal">
          <div className="customModalHeader">{title || t('dqPickTitle')}</div>
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('dqSearchPh')}
              value={query}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
            />
            {query ? (
              <button type="button" className="st-search-clear" onClick={() => setQuery('')}>
                ×
              </button>
            ) : null}
          </div>

          {loadError ? <div className="st-field-error">{loadError}</div> : null}

          {loading ? (
            <div className="dq-picker-loading">
              <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
              <p>{t('ttLoadingPlayers')}</p>
            </div>
          ) : (
            <div className="dq-picker-list">
              {players.length ? players.map((player) => {
                const selected = value && Number(value.id) === Number(player.id);
                return (
                  <button
                    type="button"
                    key={player.id}
                    className={`dq-picker-item ${selected ? 'selected' : ''}`.trim()}
                    onClick={() => {
                      onChange(player);
                      close();
                    }}
                  >
                    <img
                      src={playerPhoto(player)}
                      alt=""
                      onError={(event) => { event.target.src = defaultPlayerImage; }}
                    />
                    <span>
                      <strong>{playerFullName(player, t('dqPlayerFallback', { id: player.id }))}</strong>
                      <em>{playerMeta(player)}</em>
                    </span>
                  </button>
                );
              }) : (
                <div className="dq-picker-empty">
                  {searched ? t('ttNoPlayers') : t('ttSearchHint')}
                </div>
              )}
            </div>
          )}
        </div>
      </CustomModal>
    </>
  );
}
